import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import heroImage from '../../../assets/christmas25/grotto.webp';
import festiveImage from '../../../assets/christmas25/festive.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;

  @media (max-width: 768px) {
    height: clamp(520px, 82svh, 700px);
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to top,
      rgba(16,29,64,0.94) 0%,
      rgba(16,29,64,0.62) 32%,
      rgba(16,29,64,0.18) 60%,
      transparent 100%
    );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;

  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const HeroBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    a { width: 100%; text-align: center; }
  }
`;

const BtnPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const BtnGhost = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.65);
  background: rgba(255,255,255,0.07);
  color: #fff;
  text-decoration: none;
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};

  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(2rem, 4vw, 3rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};

  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.1);
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 1.4rem;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const Notice = styled.div`
  background: rgba(201,150,60,0.08);
  border-left: 4px solid ${C.gold};
  border-radius: 0 8px 8px 0;
  padding: 1rem 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${C.navy};
  font-weight: 600;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const ImgStrip = styled.div`
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 30%;
    display: block;
  }
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
  flex: 1;
  min-width: min(100%, 16rem);
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;

  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

const SantasGrottoRetailVillageOptions = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Santa's Grotto at Belvoir Retail Village 2026 | Christmas Events</title>
        <meta name="description" content="Meet Father Christmas in his magical grotto at Belvoir Retail Village this Christmas. A heartwarming family visit with a present for each child." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/santas-grotto-retail-village-options" />
        <meta property="og:title" content="Santa's Grotto at Belvoir Retail Village 2026" />
        <meta property="og:description" content="Meet Father Christmas in his magical grotto at Belvoir Retail Village — a heartwarming family visit with a present for each child." />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/santas-grotto-retail-village-options" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Santa's Grotto at Belvoir Retail Village 2026" />
        <meta name="twitter:description" content="Meet Father Christmas at Belvoir Retail Village — a heartwarming family grotto visit with a present for each child." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      {/* HERO */}
      <HeroWrap>
        <HeroImg src={heroImage} alt="Santa's Grotto at Belvoir Retail Village" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Belvoir Retail Village · Christmas 2026</HeroKicker>
          <HeroTitle>
            Santa's Grotto <em>at the Village</em>
          </HeroTitle>
          <HeroStrap>
            A magical visit with Father Christmas at Belvoir Retail Village — a heartwarming festive moment for the whole family
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Retail Village</Pill>
            <Pill>Present for Every Child</Pill>
            <Pill>No Castle Entry Required</Pill>
          </BadgeRow>
          <HeroBtns>
            <BtnPrimary href="/booking-journey?locationId=978&pageId=d4314a7f-aa55-4e17-994e-f58655da7072">
              Book Santa's Grotto
            </BtnPrimary>
            <BtnGhost href="/christmas/santas-grotto-options">
              See All Grotto Options
            </BtnGhost>
          </HeroBtns>
        </HeroContent>
      </HeroWrap>

      {/* ABOUT */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Experience</Kicker>
              <Divider />
              <SectionTitle>Father Christmas,<br /><em>at the Retail Village</em></SectionTitle>
              <SectionLead>
                Step into a beautifully decorated festive grotto at Belvoir Retail Village and meet Father Christmas himself. A warm, unhurried family visit — perfect for little ones at Christmas.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                Each child receives a special present from Santa, and you'll have time to enjoy the moment together before exploring the Retail Village's shops, dining and festive atmosphere. A lovely experience on its own, or pair it with a full day at the Castle.
              </SectionLead>
              <Notice style={{ marginTop: '1.75rem' }}>
                This grotto is at Belvoir Retail Village — just a 5-minute walk from Belvoir Castle. No Castle entry required to visit. Want to combine it with Deck the Halls?{' '}
                <Link to="/christmas/santas-grotto-options" style={{ color: C.navy }}>See all grotto options →</Link>
              </Notice>
              <MidCta style={{ marginTop: '1.5rem' }}>
                <MidCtaText>Book your Retail Village grotto visit</MidCtaText>
                <MidCtaBtn href="/booking-journey?locationId=978&pageId=d4314a7f-aa55-4e17-994e-f58655da7072">Book Now</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={festiveImage} alt="Christmas at Belvoir" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      {/* WHAT'S INCLUDED */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker>What's Included</Kicker>
          <Divider />
          <SectionTitle>Everything Your <em>Family Gets</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon>🎅</FeatureIcon>
              <FeatureTitle>Private Family Visit with Santa</FeatureTitle>
              <FeatureBody>A dedicated, unhurried visit with Father Christmas in his beautifully dressed festive grotto at Belvoir Retail Village — a magical moment for children of all ages.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🎁</FeatureIcon>
              <FeatureTitle>A Special Present</FeatureTitle>
              <FeatureBody>Each child receives a special gift from Father Christmas — a keepsake from a warm and memorable festive visit.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>📸</FeatureIcon>
              <FeatureTitle>Photo Opportunities</FeatureTitle>
              <FeatureBody>The grotto setting is beautifully decorated for the season — plenty of opportunity to capture the magic and the moment with your family.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon>🛍️</FeatureIcon>
              <FeatureTitle>Explore the Retail Village</FeatureTitle>
              <FeatureBody>After your visit, enjoy the Belvoir Retail Village — shops, dining, and festive atmosphere just a short walk from the Castle.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon>🏰</FeatureIcon>
              <FeatureTitle $light>Want to Add the Castle?</FeatureTitle>
              <FeatureBody $light>Combine your grotto visit with a full Deck the Halls day out — book Castle entry separately and add Santa's Grotto at checkout, or visit the Castle Grotto instead.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>

          <MidCta>
            <MidCtaText>Looking for Santa's Grotto inside the Castle itself?</MidCtaText>
            <MidCtaBtn href="/christmas/santas-grotto-castle">Castle Grotto →</MidCtaBtn>
          </MidCta>
        </Inner>
      </Section>

      {/* INFO GRID */}
      <Section $bg={C.parchment} $border>
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Belvoir Retail Village</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Castle Entry</InfoLabel>
              <InfoValue>Not required for this experience</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Included</InfoLabel>
              <InfoValue>Private visit, present for each child</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Distance from Castle</InfoLabel>
              <InfoValue>5-minute walk</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Family Saver</InfoLabel>
              <InfoValue>Available on Castle entry (separate)</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>Belvoir Retail Village<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      {/* NEWSLETTER */}
      <div style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      {/* CTA */}
      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Book Your <em>Grotto Visit</em>
          </CtaTitle>
          <CtaBody>
            Father Christmas is waiting at Belvoir Retail Village. Book your family's private grotto visit online — and make it a Christmas moment they'll always remember.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href="/booking-journey?locationId=978&pageId=d4314a7f-aa55-4e17-994e-f58655da7072">
              Book Now
            </CtaBtn>
            <CtaSecondary to="/christmas/santas-grotto-options">
              All Grotto Options
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Belvoir Retail Village · Pre-booking recommended
          </CtaMeta>
        </CtaInner>
      </CtaSection>
    </Page>
  );
};

export default SantasGrottoRetailVillageOptions;
